import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Building2,
  CreditCard,
  DollarSign,
  Download,
  GraduationCap,
  History,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  Sparkles,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/AuthContext";
import DateRangeBar from "@/components/DateRangeBar";
import TrialNotice from "@/components/TrialNotice";
import SupportWidget from "@/support/SupportWidget";
import { LOGOUT } from "@/constants/testIds/auth";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/install", label: "Install Extension", icon: Download },
  { to: "/history", label: "History", icon: History },
  { to: "/goals", label: "Goals", icon: Target },
  { to: "/companies", label: "Companies", icon: Building2 },
  { to: "/pay-rates", label: "Pay Rates", icon: DollarSign },
  { to: "/education", label: "Education", icon: GraduationCap },
  { to: "/ai", label: "AI", icon: Sparkles, soon: true },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: Settings },
];

function NavItems({ onNavigate }) {
  return (
    <nav className="mpd-nav">
      {NAV.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `mpd-nav-link ${isActive ? "is-active" : ""}`}
            onClick={onNavigate}
          >
            <Icon size={18} strokeWidth={1.8} />
            <span>{item.label}</span>
            {item.soon ? <em>Soon</em> : null}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const first = (user?.full_name || user?.email || "M").trim().charAt(0).toUpperCase();
  const locked = user?.role !== "admin" && user?.has_access !== true;

  function onLogout() {
    logout();
    navigate("/login");
  }

  return (
    <div className="mpd-app">
      {locked ? null : <TrialNotice />}
      <aside className="mpd-sidebar">
        <div className="mpd-brand">
          <span className="mpd-mark">M</span>
          <div>
            <strong>MinutePay</strong>
            <p>Interpreter income</p>
          </div>
        </div>
        {locked ? null : <NavItems />}
        <div className="mpd-sidebar-foot">
          <div className="mpd-user">
            <span className="mpd-avatar">{first}</span>
            <div>
              <strong>{user?.full_name || "Account"}</strong>
              <p>{user?.email}</p>
            </div>
          </div>
          {user?.role === "admin" ? (
            <Button variant="outline" className="w-full" onClick={() => navigate("/admin")}>
              Admin
            </Button>
          ) : null}
          <Button data-testid={LOGOUT.button} variant="outline" className="w-full" onClick={onLogout}>
            <LogOut size={16} /> Log out
          </Button>
        </div>
      </aside>

      <div className="mpd-main">
        <header className="mpd-top">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="mpd-menu" type="button" aria-label="Open menu">
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="mpd-sheet">
              <div className="mpd-brand mpd-brand-sheet">
                <span className="mpd-mark">M</span>
                <strong>MinutePay</strong>
              </div>
              {locked ? null : <NavItems onNavigate={() => setOpen(false)} />}
            </SheetContent>
          </Sheet>
          <div className="mpd-top-right">
            {locked ? null : <DateRangeBar />}
          </div>
        </header>
        <div className="mpd-content">
          <Outlet />
        </div>
      </div>
      <SupportWidget />
    </div>
  );
}
